import React, { useState,useEffect,useCallback,useMemo,useRef,
} from "react";
import { createPortal } from "react-dom";
import "./Sidebar.css";

import {
  MessageCircle,
  History,
  Settings,
  ChevronDown,
  ChevronRight,
  X,
  LogOut,
  Bookmark,
  HelpCircle,
  MessagesSquare,
  Camera,
  Plus,
  MoreVertical,
  Star,
  Pencil,
  Trash2,
  Check,
} from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";
import { hasPermission } from "../../utils/permissionUtils";
import { toast } from "react-toastify";
import Logo from "../../assets/Genielytics.svg";
import api from "../../apiWithAccessToken";
import { PanelLeftClose, PanelLeftOpen, Database, LayoutDashboard } from "lucide-react";
import ProfileImageModal from "./ProfileImageModal";
import { listSavedDashboards, deleteSavedDashboard, renameSavedDashboard } from "../../apiDashboard";
import { readScopedValue, writeScopedValue } from "../../utils/scopedStorage";


// ─── Dashboard Item with ⋮ context menu ──────────────────────────────────────

const DashboardContextMenu = ({ anchorRef, onClose, onStar, onRename, onDelete, isStarred }) => {
  const menuRef = useRef(null);
  // Start offscreen so first paint does not flash at 0,0
  const [pos, setPos] = useState({ top: -9999, left: -9999 });

  // Calculate position after the menu has mounted and has a real measured size
  useEffect(() => {
    if (!anchorRef.current || !menuRef.current) return;
    const btn  = anchorRef.current.getBoundingClientRect();
    const menu = menuRef.current.getBoundingClientRect();
    const vw   = window.innerWidth;
    const vh   = window.innerHeight;

    // Prefer opening to the RIGHT of the dots button
    let left = btn.right + 8;
    // Flip to left if it would overflow the right edge
    if (left + menu.width > vw - 8) left = btn.left - menu.width - 8;

    // Align top with the button, clamp to viewport
    let top = btn.top;
    if (top + menu.height > vh - 8) top = vh - menu.height - 8;
    if (top < 8) top = 8;

    setPos({ top, left });
  }, [anchorRef]);

  // Close on outside click or Escape
  useEffect(() => {
    const handleClick = (e) => {
      if (
        menuRef.current &&
        !menuRef.current.contains(e.target) &&
        !anchorRef.current?.contains(e.target)
      ) {
        onClose();
      }
    };
    const handleKey = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("mousedown", handleClick);
    document.addEventListener("keydown", handleKey);
    return () => {
      document.removeEventListener("mousedown", handleClick);
      document.removeEventListener("keydown", handleKey);
    };
  }, [onClose, anchorRef]);

  // Portal into document.body so no parent stacking context clips it
  return createPortal(
    <div ref={menuRef} className="sb-ctx-menu" style={{ top: pos.top, left: pos.left }} role="menu">
      <button type="button" className="sb-ctx-item" onClick={() => { onStar(); onClose(); }}>
        <Star size={14} className={isStarred ? "sb-ctx-icon--starred" : "sb-ctx-icon"} />
        <span>{isStarred ? "Unstar" : "Star"}</span>
      </button>
      <button type="button" className="sb-ctx-item" onClick={() => { onRename(); onClose(); }}>
        <Pencil size={14} className="sb-ctx-icon" />
        <span>Rename</span>
      </button>
      <div className="sb-ctx-divider" />
      <button type="button" className="sb-ctx-item sb-ctx-item--danger" onClick={() => { onDelete(); onClose(); }}>
        <Trash2 size={14} />
        <span>Delete</span>
      </button>
    </div>,
    document.body
  );
};

const DashboardSidebarItem = ({ dashboard, isActive, onOpen, onDeleted, onRenamed }) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [isStarred, setIsStarred] = useState(false);
  const [renaming, setRenaming] = useState(false);
  const [renameValue, setRenameValue] = useState(dashboard.title || "");
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [displayTitle, setDisplayTitle] = useState(dashboard.title || "Untitled");
  const dotsBtnRef = useRef(null);
  const renameInputRef = useRef(null);

  useEffect(() => {
    if (renaming) renameInputRef.current?.focus();
  }, [renaming]);

  const handleStar = useCallback(() => {
    setIsStarred((p) => !p);
    toast.info(!isStarred ? "Starred!" : "Unstarred.");
  }, [isStarred]);

  const handleRenameStart = useCallback(() => {
    setRenameValue(displayTitle);
    setRenaming(true);
  }, [displayTitle]);

  const handleRenameSubmit = useCallback(async () => {
    const trimmed = renameValue.trim();
    if (!trimmed || trimmed === displayTitle) { setRenaming(false); return; }
    try {
      await renameSavedDashboard(dashboard.id, trimmed);
      setDisplayTitle(trimmed);
      onRenamed?.(dashboard.id, trimmed);
      toast.success("Renamed successfully.");
    } catch (err) {
      toast.error(err.message || "Rename failed.");
    } finally {
      setRenaming(false);
    }
  }, [renameValue, displayTitle, dashboard.id, onRenamed]);

  const handleDeleteConfirm = useCallback(async () => {
    setIsDeleting(true);
    try {
      await deleteSavedDashboard(dashboard.id);
      onDeleted?.(dashboard.id);
      window.dispatchEvent(new CustomEvent("dashboard-recents-updated", { detail: { deletedId: dashboard.id } }));
      toast.success("Dashboard deleted.");
    } catch (err) {
      toast.error(err.message || "Delete failed.");
    } finally {
      setIsDeleting(false);
      setConfirmDelete(false);
    }
  }, [dashboard.id, onDeleted]);

  return (
    <li
      className={`sidebar-item group relative transition-all ${isActive ? "active" : "hover:bg-muted"} ${isDeleting ? "sb-item--deleting" : ""}`}
      title={!renaming ? displayTitle : undefined}
    >
      {isStarred && <span className="sb-star-dot" />}

      {renaming ? (
        /* ── Inline rename input ── */
        <div className="sb-rename-row" onClick={(e) => e.stopPropagation()}>
          <input
            ref={renameInputRef}
            className="sb-rename-input"
            value={renameValue}
            onChange={(e) => setRenameValue(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") handleRenameSubmit();
              if (e.key === "Escape") setRenaming(false);
            }}
          />
          <button type="button" className="sb-rename-btn sb-rename-btn--ok" onClick={handleRenameSubmit} title="Confirm">
            <Check size={11} />
          </button>
          <button type="button" className="sb-rename-btn sb-rename-btn--cancel" onClick={() => setRenaming(false)} title="Cancel">
            <X size={11} />
          </button>
        </div>
      ) : (
        /* ── Normal row ── */
        <div className="flex items-center gap-2 overflow-hidden min-w-0 w-full">
          <LayoutDashboard size={14} className="shrink-0 text-muted-foreground" />
          <span
            className={`text-xs font-medium truncate cursor-pointer session-title flex-1 ${isActive ? "text-primary" : "text-foreground"}`}
            onClick={() => onOpen(dashboard.id)}
          >
            {displayTitle}
          </span>

          {/* ⋮ button — visible on hover or when menu is open */}
          <button
            ref={dotsBtnRef}
            type="button"
            className={`sb-dots-btn ${menuOpen ? "sb-dots-btn--open" : ""}`}
            onClick={(e) => { e.stopPropagation(); setMenuOpen((p) => !p); }}
            title="Options"
            aria-haspopup="true"
            aria-expanded={menuOpen}
          >
            <MoreVertical size={14} />
          </button>
        </div>
      )}

      {/* Floating context menu */}
      {menuOpen && (
        <DashboardContextMenu
          anchorRef={dotsBtnRef}
          onClose={() => setMenuOpen(false)}
          isStarred={isStarred}
          onStar={handleStar}
          onRename={handleRenameStart}
          onDelete={() => setConfirmDelete(true)}
        />
      )}

      {/* Full-screen delete confirm modal */}
      {confirmDelete && createPortal(
        <div className="sb-delete-modal-overlay" onClick={() => setConfirmDelete(false)}>
          <div className="sb-delete-modal" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
            <h2 className="sb-delete-modal__title">Delete dashboard?</h2>
            <p className="sb-delete-modal__body">
              This will permanently delete <strong>"{displayTitle}"</strong>.<br />
              This action cannot be undone.
            </p>
            <div className="sb-delete-modal__actions">
              <button
                type="button"
                className="sb-delete-modal__btn sb-delete-modal__btn--cancel"
                onClick={() => setConfirmDelete(false)}
                disabled={isDeleting}
              >
                Cancel
              </button>
              <button
                type="button"
                className="sb-delete-modal__btn sb-delete-modal__btn--delete"
                onClick={handleDeleteConfirm}
                disabled={isDeleting}
              >
                {isDeleting ? "Deleting…" : "Delete"}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </li>
  );
};


// ─── Sidebar ──────────────────────────────────────────────────────────────────

const Sidebar = ({
  activeSession,
  registerRefresh,
  onSelectChat,
  onNewChat,
  user,
  onUserUpdated,
  refreshCurrentUser,
  onLogout,
  activeSessionId,
  refreshTrigger,
  currentTitle,
  selectedDatabaseId,
  storageScope,
  collapsed,
  setCollapsed,
  isOpen = false,
  setIsOpen,
  className = "",
}) => {

  
  const [recentItemsVisible, setRecentItemsVisible] = useState(true);
  
  
const [sessions, setSessions] = useState([]);
const [dashboardSessions, setDashboardSessions] = useState([]);

const [isMobile, setIsMobile] = useState(() => window.matchMedia("(max-width: 768px)").matches);

  useEffect(() => {
    const mediaQuery = window.matchMedia("(max-width: 768px)");
    const handler = (e) => setIsMobile(e.matches);
    mediaQuery.addEventListener("change", handler);
    return () => mediaQuery.removeEventListener("change", handler);
  }, []);

  useEffect(() => {
    if (isMobile && isOpen) {
      setCollapsed(false);
    }
  }, [isMobile, isOpen, setCollapsed]);

  const navigate = useNavigate();
    const location = useLocation();

    
const isNewChatActive =
  activeSessionId?.startsWith("temp_") === true &&
  location.pathname === "/chatwindow";

const activeDashboardSearch = useMemo(() => new URLSearchParams(location.search), [location.search]);
const activeDashboardId = activeDashboardSearch.get("dashboardId");
const isNewDashboardActive = location.pathname === "/dashboard" && !activeDashboardId;

const isSettingsActive = location.pathname.startsWith("/settings");
const isDashboardActive = location.pathname.startsWith("/dashboard");

const isConversationsActive = location.pathname === "/conversations";
const [preferredMode, setPreferredMode] = useState(
  () =>
    readScopedValue(storageScope, "sidebar_mode") ||
    (isDashboardActive ? "dashboard" : "analysis")
);
const isAnalysisRoute =
  location.pathname === "/chatwindow" || location.pathname === "/conversations";
const activeMode = isDashboardActive ? "dashboard" : isAnalysisRoute ? "analysis" : preferredMode;
const modeDescription =
  activeMode === "dashboard"
    ? "Generate polished executive dashboards from prompts."
    : "Analyze business data with natural language.";

  useEffect(() => {
    writeScopedValue(storageScope, "sidebar_mode", activeMode);
  }, [activeMode, storageScope]);


  // Generate initials from name
  const getInitials = (name = "") =>
    name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .slice(0, 2)
      .toUpperCase();
  const [profileMenuOpen, setProfileMenuOpen] = useState(false);
  const [photoModalOpen, setPhotoModalOpen] = useState(false);
  const profileMenuRef = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (
        profileMenuRef.current &&
        !profileMenuRef.current.contains(e.target)
      ) {
        setProfileMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

 const userRole = String(user?.role || "").trim().toLowerCase();

const canAccessSettings = useMemo(() => {
  return (
    hasPermission(userRole, "smtp_configuration") &&
    hasPermission(userRole, "database_configuration")
  );
}, [userRole]);

const canAccessDashboard = useMemo(() => {
  return hasPermission(userRole, "access_dashboard");
}, [userRole]);

const canAccessChats = useMemo(() => {
  return hasPermission(userRole, "chat_access") || true;
}, [userRole]);

  // Include active temp session in list
  const allSessions = useMemo(() => {
    if (activeSession?.id?.startsWith("temp_")) {
      return [
        activeSession,
        ...sessions.filter((s) => s.id !== activeSession.id),
      ];
    }
    return sessions;
  }, [sessions, activeSession]);


  const latestSessions = useMemo(() => {
  return [...allSessions]
    .sort((a, b) => new Date(b.updated_at) - new Date(a.updated_at))
    .slice(0, 6);
}, [allSessions]);



  const fetchSessions = useCallback(async () => {
    try{
   const { data} = await api.get("/sessions");
   
   setSessions(Array.isArray(data) ? data : []);
    } catch (err) {
    console.error("Failed to load sessions",err);
     toast.error("Failed to load conversations");
    }
  }, []);

  const fetchDashboardSessions = useCallback(async () => {
    if (!canAccessDashboard) return;

    try {
      const response = selectedDatabaseId
        ? await listSavedDashboards({
            sourceDbId: Number(selectedDatabaseId),
          })
        : await listSavedDashboards();
      setDashboardSessions(Array.isArray(response?.dashboards) ? response.dashboards : []);
    } catch (err) {
      console.error("Failed to load dashboards", err);
      toast.error("Failed to load dashboards");
    }
  }, [canAccessDashboard, selectedDatabaseId]);


  useEffect(() => {
    fetchSessions();
  },
   [fetchSessions, refreshTrigger]);

  useEffect(() => {
    fetchDashboardSessions();
  }, [fetchDashboardSessions]);
  
useEffect(() => {
  registerRefresh?.(fetchSessions);
}, [fetchSessions, registerRefresh]);

useEffect(() => {
  const handleDashboardRefresh = (event) => {
    const eventDbId = event?.detail?.sourceDbId;
    if (
      eventDbId === undefined ||
      eventDbId === null ||
      String(eventDbId) === String(selectedDatabaseId)
    ) {
      fetchDashboardSessions();
    }
  };

  window.addEventListener("dashboard-recents-updated", handleDashboardRefresh);
  return () => window.removeEventListener("dashboard-recents-updated", handleDashboardRefresh);
}, [fetchDashboardSessions, selectedDatabaseId]);

const handleSelectSession = useCallback(
  (session) => {
    onSelectChat?.(session);

    if (!onSelectChat) {
      navigate("/chatwindow");
    }

    if (isOpen) setIsOpen(false);
  },
  [onSelectChat, navigate, isOpen, setIsOpen]
);



const handleCreateNewSession = useCallback(() => {
  setPreferredMode("analysis");
  const tempSessionId = `temp_${Date.now()}`;
  onNewChat?.({ id: tempSessionId });

  if (isOpen) setIsOpen(false);
}, [onNewChat, isOpen, setIsOpen]);

const handleCreateNewDashboard = useCallback(() => {
  setPreferredMode("dashboard");
  navigate(`/dashboard?new=${Date.now()}`);
  setRecentItemsVisible(true);
  if (isOpen) setIsOpen(false);
}, [navigate, isOpen, setIsOpen]);

const handleSelectDashboard = useCallback((dashboardId) => {
  setPreferredMode("dashboard");
  navigate(`/dashboard?dashboardId=${dashboardId}`);
  if (isOpen) setIsOpen(false);
}, [navigate, isOpen, setIsOpen]);

const handleSwitchMode = useCallback((mode) => {
  setPreferredMode(mode);
  if (mode === "dashboard") {
    navigate("/dashboard");
  } else if (activeSessionId) {
    navigate("/chatwindow");
  } else {
    navigate("/conversations");
  }

  if (isOpen) setIsOpen(false);
}, [activeSessionId, navigate, isOpen, setIsOpen]);

const handleProfilePhotoSaved = useCallback(
  async (nextUser) => {
    onUserUpdated?.(nextUser);
    await refreshCurrentUser?.();
  },
  [onUserUpdated, refreshCurrentUser]
);

// Dashboard item callbacks
const handleDashboardDeleted = useCallback((id) => {
  setDashboardSessions((prev) => prev.filter((d) => d.id !== id));
  // If the deleted dashboard was active, navigate away
  if (String(activeDashboardId) === String(id)) {
    navigate("/dashboard");
  }
}, [activeDashboardId, navigate]);

const handleDashboardRenamed = useCallback((id, newTitle) => {
  setDashboardSessions((prev) =>
    prev.map((d) => d.id === id ? { ...d, title: newTitle } : d)
  );
}, []);


  const getSessionTitle = (session) => {
    if (session?.title?.trim()) return session.title;
    if (session?.id === activeSessionId && currentTitle) return currentTitle;
    return "New Chat";
  };

  return (
    <>
      {isOpen && (
        <div
          className={`mobile-overlay ${isOpen ? "mobile-open" : ""}`}
          onClick={() => setIsOpen(false)}
        />
      )}
      <div
        className={`sidebar ${collapsed ? "collapsed" : ""} ${
          isOpen ? "mobile-open" : ""
        } ${profileMenuOpen ? "profile-menu-open" : ""} ${className}`}
      >
        <div className="flex items-center justify-between px-4 py-3 bg-surface backdrop-blur ">
          <div className="flex items-center space-x-2">
            <img src={Logo} alt="Logo" className="sidebar-logo" />
           
          </div>
        <div className="flex items-center space-x-2">
          <button
              onClick={() => setIsOpen(false)}
              className="md:hidden p-2 rounded-m hover:bg-muted text-foreground"
              aria-label="Close sidebar"
            >
              <X size={18} />
          </button>

          <button
             onClick={() => {
             if (!isMobile) {
           setCollapsed(c => !c);
            }
       }}
  className="collapse-btn p-2 rounded-lg hover:bg-muted text-muted-foreground"
  data-tooltip={collapsed ? "Expand Sidebar" : "Collapse Sidebar"}
>
  {collapsed ? <PanelLeftOpen size={18} /> : <PanelLeftClose size={18} />}
   </button>

</div></div>

        <nav className="sidebar-nav">
          <div className="sidebar-nav__top">
            {canAccessDashboard && (
              <div className={`sidebar-mode-shell ${collapsed ? "collapsed" : ""}`}>
                <div className={`sidebar-mode-toggle ${collapsed ? "collapsed" : ""}`}>
                  <button
                    type="button"
                    onClick={() => handleSwitchMode("analysis")}
                    className={`sidebar-mode-toggle__button ${activeMode === "analysis" ? "is-active" : ""}`}
                    data-tooltip={collapsed ? "Analysis Mode" : undefined}
                  >
                    {collapsed ? <MessagesSquare className="w-4 h-4" /> : <span>Analysis</span>}
                  </button>
                  <button
                    type="button"
                    onClick={() => handleSwitchMode("dashboard")}
                    className={`sidebar-mode-toggle__button ${activeMode === "dashboard" ? "is-active" : ""}`}
                    data-tooltip={collapsed ? "Dashboard Mode" : undefined}
                  >
                    {collapsed ? <LayoutDashboard className="w-4 h-4" /> : <span>Dashboard</span>}
                  </button>
                </div>
                {!collapsed && <p className="sidebar-mode-copy">{modeDescription}</p>}
              </div>
            )}

            <div className="sidebar-nav__actions">
              {activeMode === "analysis" && (
                <>
                  <button
                    onClick={handleCreateNewSession}
                    className={`sidebar-item tooltip flex items-center gap-3 rounded
                      ${collapsed ? "justify-center" : ""}
                      ${isNewChatActive ? "active" : "hover:bg-muted"}`}
                    data-tooltip="New Chat"
                  >
                    <MessageCircle className="w-5 h-5" />
                    {!collapsed && <span className="text-sm">New Chat</span>}
                  </button>

                  {canAccessChats && (
                    <button
                      onClick={() => {
                        navigate("/conversations");
                        if (isOpen) setIsOpen(false);
                      }}
                      className={`sidebar-item tooltip flex items-center gap-3 rounded
                        ${isConversationsActive ? "active" : "hover:bg-muted"}
                        ${collapsed ? "justify-center" : ""}`}
                      data-tooltip="Chats"
                    >
                      <MessagesSquare className="w-5 h-5" />
                      {!collapsed && <span className="text-sm">Chats</span>}
                    </button>
                  )}
                </>
              )}

              {activeMode === "dashboard" && canAccessDashboard && (
                <button
                  onClick={handleCreateNewDashboard}
                  className={`sidebar-item tooltip flex items-center gap-3 rounded
                    ${collapsed ? "justify-center" : ""}
                    ${isNewDashboardActive ? "active" : "hover:bg-muted"}`}
                  data-tooltip="New Dashboard"
                >
                  <Plus className="w-5 h-5" />
                  {!collapsed && <span className="text-sm">New Dashboard</span>}
                </button>
              )}

              {collapsed && (
                <button
                  onClick={() =>
                     setCollapsed(false)}
                  className="tooltip flex items-center gap-3 hover:bg-muted rounded justify-center collapsed-history"
                  data-tooltip="Conversations"
                >
                  <History className="w-5 h-5" />
                </button>
              )}
            </div>
          </div>

          {!collapsed && (
            <div className="sidebar-recents-section">
              <button
                onClick={() => setRecentItemsVisible((v) => !v)}
                className={`sidebar-item tooltip flex items-center gap-3 rounded ${
                  recentItemsVisible ? "recent-open" : "hover:bg-muted"
                }`}
                data-tooltip={activeMode === "dashboard" ? "Recent Dashboards" : "Recent Chats"}
              >
                <History className="w-5 h-5 " />
                <span className="text-sm flex-1 text-left">
                  {activeMode === "dashboard" ? "Recent Dashboards" : "Recent Chats"}
                </span>
                {recentItemsVisible ? (
                  <ChevronDown className="w-4 h-4" />
                ) : (
                  <ChevronRight className="w-4 h-4" />
                )}
              </button>

              {recentItemsVisible && (
                <div className="conversation-scroll">
                  <ul className="session-list divide-y divide-border h-full">
                  {activeMode === "dashboard"
                    ? (
                        dashboardSessions.length > 0 ? (
                          dashboardSessions.map((dashboard) => (
                            <DashboardSidebarItem
                              key={dashboard.id}
                              dashboard={dashboard}
                              isActive={String(activeDashboardId) === String(dashboard.id)}
                              onOpen={handleSelectDashboard}
                              onDeleted={handleDashboardDeleted}
                              onRenamed={handleDashboardRenamed}
                            />
                          ))
                        ) : (
                          <li className="px-3 py-6 text-center text-muted-foreground">
                            {selectedDatabaseId
                              ? "No dashboards yet — generate one to get started."
                              : "Open the pinned demo or select a dashboard database."}
                          </li>
                        )
                      )
                    : (
                        latestSessions.length > 0 ? (
                          latestSessions.map((s) => {
                            const isActive = activeSessionId === s.id;

                            return (
                              <li
                                key={s.id}
                                className={`sidebar-item group relative px-3 py-2 transition-all ${
                                  isActive ? "active" : "hover:bg-muted"
                                }`}
                                title={getSessionTitle(s)}
                              >
                                <div className="flex justify-between items-center">
                                  <div className="flex items-center gap-2 overflow-hidden">
                                    <div className="flex items-center gap-1 overflow-hidden">
                                      {s.is_bookmarked && (
                                        <Bookmark
                                          size={12}
                                          className="text-primary fill-current shrink-0"
                                        />
                                      )}

                                      <span
                                        className={`text-xs font-medium truncate cursor-pointer session-title ${
                                          isActive ? "text-primary" : "text-foreground"
                                        }`}
                                        onClick={() => handleSelectSession(s)}
                                      >
                                        {getSessionTitle(s)}
                                      </span>
                                    </div>
                                  </div>
                                </div>
                              </li>
                            );
                          })
                        ) : (
                          <li className="px-3 py-6 text-center text-muted-foreground">
                            No conversations yet — start a new chat.
                          </li>
                        )
                      )}
                  
                </ul>
              </div>
              )}
            </div>
          )}

          <div className="sidebar-nav__bottom">
            <button
              onClick={() => {
                navigate("/datadump");
                if (isOpen) setIsOpen(false);
              }}

              className={`sidebar-item tooltip flex items-center gap-3 rounded
                ${collapsed ? "justify-center" : ""}
                ${location.pathname === "/datadump" ? "active" : "hover:bg-muted"}`}
              data-tooltip="Data Export"
            >
              <Database className="w-5 h-5" />
              {!collapsed && <span className="text-sm">Data Export</span>}
            </button>
        
            {canAccessSettings && (
              <button
                onClick={() => {
                  navigate("/settings");
                  if (isOpen) setIsOpen(false);
                }}
                className={`sidebar-item tooltip flex items-center gap-3 rounded
                  ${collapsed ? "justify-center" : ""}
                  ${isSettingsActive ? "active" : "hover:bg-muted"}`}
                data-tooltip="Settings"
              >
                <Settings className="w-5 h-5" />
                {!collapsed && <span className="text-sm">Settings</span>}
              </button>
            )}
          </div>
        </nav>

     {user && (
    <div className="sidebar-footer mt-auto" ref={profileMenuRef}>
            
           <button
  className={`profile-btn ${collapsed ? "collapsed" : ""}`}
  onClick={() => setProfileMenuOpen(!profileMenuOpen)}
  data-tooltip={collapsed ? user.name : undefined}
>
  {user.avatar ? (
    <img src={user.avatar} alt="User avatar" className="profile-avatar" />
  ) : (
    <div className="profile-initials">{getInitials(user.name)}</div>
  )}

  {!collapsed && (
    <span className="profile-name">{user.name}</span>
  )}
</button>

    {profileMenuOpen && (
  <div
    className={`profile-menu ${collapsed ? "collapsed" : ""}`}
  >
    <div className="profile-divider" />


<button
  onClick={() => {
    setPhotoModalOpen(true);
    setProfileMenuOpen(false);
    if (isOpen) setIsOpen(false);
  }}
  className="flex items-center gap-2 px-3 py-2 hover:bg-muted rounded-md"
>
  <Camera size={16} />
  <span>Change Profile Photo</span>
</button>

<button
  onClick={() => navigate("/support")}
  className="flex items-center gap-2 px-3 py-2 hover:bg-muted rounded-md"
>
        <HelpCircle size={16} />

  <span>Help & Support</span>
</button>


<button
  className="logout-btn"
  onClick={() => {
    onLogout?.("manual");
    setProfileMenuOpen(false);
    if (isOpen) setIsOpen(false);
  }}
>

      <LogOut size={16} />
      <span>Logout</span>
    </button>
 </div>
)}

  </div>
 )}
</div>

<ProfileImageModal
  isOpen={photoModalOpen}
  onClose={() => setPhotoModalOpen(false)}
  onSaved={handleProfilePhotoSaved}
  currentUser={user}
/>
    

    </>
  );
};

export default Sidebar;
